import { KeyRound, Plus, Power } from "lucide-react";
import { useState } from "react";
import {
  rotateB2bPartnerKeyAdmin,
  setB2bPartnerActiveAdmin,
  useAction,
} from "wasp/client/operations";
import { Badge } from "../../../client/components/ui/badge";
import { Button } from "../../../client/components/ui/button";
import { Card, CardContent } from "../../../client/components/ui/card";
import { useToast } from "../../../client/hooks/use-toast";
import useTranslation from "../../../client/hooks/useTranslation";
import { KeyRevealDialog, NewPartnerDialog } from "./dialogs";

export interface PartnerRow {
  id: string;
  name: string;
  isActive: boolean;
  contactEmail: string | null;
  createdAt: string;
  counts: {
    billable: number;
    pending: number;
    active: number;
    shadowed: number;
    revoked: number;
  };
}

export default function PartnersCard({
  partners,
  onChanged,
}: {
  partners: PartnerRow[];
  onChanged: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const rotateKey = useAction(rotateB2bPartnerKeyAdmin);
  const setActive = useAction(setB2bPartnerActiveAdmin);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; apiKey: string } | null>(
    null,
  );

  const handleRotate = async (partner: PartnerRow) => {
    if (!window.confirm(t.adminB2b.rotateKeyConfirm)) return;
    try {
      const { apiKey } = await rotateKey({ partnerId: partner.id });
      setRevealed({ name: partner.name, apiKey });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  const handleToggleActive = async (partner: PartnerRow) => {
    // Seule la coupure demande confirmation : réactiver est sans risque.
    if (partner.isActive && !window.confirm(t.adminB2b.disablePartnerConfirm)) return;
    try {
      await setActive({ partnerId: partner.id, isActive: !partner.isActive });
      toast({ title: t.adminB2b.partnerUpdatedToast });
      onChanged();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t.adminB2b.partnersTitle}</h2>
          <Button size="sm" onClick={() => setShowNewDialog(true)}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            {t.adminB2b.newPartner}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-3">{t.adminB2b.partnerName}</th>
                <th className="py-2 pr-3">{t.adminB2b.contactEmail}</th>
                <th className="py-2 pr-3">{t.adminB2b.createdAt}</th>
                <th className="py-2 pr-3">{t.adminB2b.seatsBillable}</th>
                <th className="py-2 text-right">{t.adminB2b.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-border/50 ${p.isActive ? "" : "opacity-60"}`}
                >
                  <td className="py-2.5 pr-3">
                    <div className="font-medium">{p.name}</div>
                    <Badge
                      variant={p.isActive ? "default" : "destructive"}
                      className="mt-1"
                    >
                      {p.isActive ? t.adminB2b.apiActive : t.adminB2b.apiDisabled}
                    </Badge>
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground">
                    {p.contactEmail ?? "-"}
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="font-mono tabular-nums font-semibold">
                      {p.counts.billable}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Badge variant="outline">
                        {t.adminB2b.statusPending} {p.counts.pending}
                      </Badge>
                      <Badge variant="default">
                        {t.adminB2b.statusActive} {p.counts.active}
                      </Badge>
                      <Badge variant="secondary">
                        {t.adminB2b.statusShadowed} {p.counts.shadowed}
                      </Badge>
                      <Badge variant="destructive">
                        {t.adminB2b.statusRevoked} {p.counts.revoked}
                      </Badge>
                    </div>
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => handleRotate(p)}>
                      <KeyRound className="mr-1 h-3.5 w-3.5" aria-hidden />
                      {t.adminB2b.rotateKey}
                    </Button>
                    <Button
                      size="sm"
                      variant={p.isActive ? "destructive" : "outline"}
                      className="ml-2"
                      onClick={() => handleToggleActive(p)}
                    >
                      <Power className="mr-1 h-3.5 w-3.5" aria-hidden />
                      {p.isActive ? t.adminB2b.disablePartner : t.adminB2b.enablePartner}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      <NewPartnerDialog
        open={showNewDialog}
        onOpenChange={setShowNewDialog}
        onCreated={(result) => {
          setRevealed(result);
          onChanged();
        }}
      />
      <KeyRevealDialog
        apiKey={revealed?.apiKey ?? null}
        partnerName={revealed?.name ?? ""}
        open={!!revealed}
        onClose={() => setRevealed(null)}
      />
    </Card>
  );
}
